(function () {
  'use strict';

  // Bridges the product quiz to Klaviyo (onsite.js, already loaded in
  // theme.liquid via klaviyo.init)). Klaviyo's
  // client-side API has no way to look up, edit, or attach an ID to a
  // previously-tracked event — the practical equivalent of "the row that
  // gets updated" is the shopper's *profile*, keyed by email and enriched
  // via repeated identify() calls (quiz_status/quiz_current_step/etc).
  // "Send a partial if they never finish" is an abandonment-flow behavior
  // that has to be configured in Klaviyo's own Flow builder, triggered off
  // the "Started Product Quiz" metric tracked below with a time-window
  // filter for the absence of "Completed Product Quiz" — it isn't
  // something this file (or any client-side code) can set up on its own.
  //
  // This file only listens to CustomEvents already dispatched by
  // product-quiz.js — it never modifies that file or the quiz markup.
  //
  // Completions also write a numbered JSON snapshot (quiz_submission_1,
  // quiz_submission_2, …) so prior runs stay on the profile while the
  // clean quiz_* keys always reflect the latest attempt.

  var ATTEMPT_STORAGE_KEY = 'os_quiz_attempt_counts';
  var QUIZ_PROFILE_KEYS = [
    'quiz_bundle',
    'quiz_first_name',
    'quiz_phone',
    'quiz_sms_opt_in',
    'quiz_result',
    'quiz_result_title',
    'quiz_status',
    'quiz_current_step',
    'quiz_total_steps',
    'quiz_last_question',
    'quiz_last_answer'
  ];

  function klaviyoReady() {
    return typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.identify === 'function';
  }

  function extend(base, extra) {
    var result = {};
    var key;
    for (key in base) {
      if (base.hasOwnProperty(key)) result[key] = base[key];
    }
    if (extra) {
      for (key in extra) {
        if (extra.hasOwnProperty(key) && extra[key] !== undefined && extra[key] !== '') {
          result[key] = extra[key];
        }
      }
    }
    return result;
  }

  // Includes empty strings so unused path properties can be blanked on retake.
  function extendAllowEmpty(base, extra) {
    var result = {};
    var key;
    for (key in base) {
      if (base.hasOwnProperty(key)) result[key] = base[key];
    }
    if (extra) {
      for (key in extra) {
        if (extra.hasOwnProperty(key) && extra[key] !== undefined) {
          result[key] = extra[key];
        }
      }
    }
    return result;
  }

  function normalizeEmail(email) {
    return String(email || '').trim().toLowerCase();
  }

  function readAttemptCounts() {
    try {
      var raw = window.localStorage.getItem(ATTEMPT_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function nextAttemptNumber(email) {
    var key = normalizeEmail(email);
    if (!key) return 1;
    var counts = readAttemptCounts();
    var next = (parseInt(counts[key], 10) || 0) + 1;
    counts[key] = next;
    try {
      window.localStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(counts));
    } catch (e) {}
    return next;
  }

  function allQuizPropertyKeys() {
    var keys = {};
    var i;
    for (i = 0; i < QUIZ_PROFILE_KEYS.length; i++) keys[QUIZ_PROFILE_KEYS[i]] = true;
    Array.prototype.slice.call(document.querySelectorAll('[data-klaviyo-property]')).forEach(function (el) {
      var name = (el.getAttribute('data-klaviyo-property') || '').trim();
      if (name) keys[name] = true;
    });
    return Object.keys(keys);
  }

  function buildSubmissionSnapshot(detail, attempt) {
    var snapshot = {
      attempt: attempt,
      at: new Date().toISOString(),
      result: detail.tag || '',
      result_title: detail.resultTitle || '',
      first_name: detail.firstName || '',
      phone: detail.phone || '',
      sms_opt_in: detail.smsOptIn ? true : false
    };
    var props = detail.properties || {};
    Object.keys(props).forEach(function (key) {
      if (props[key] !== undefined && props[key] !== '') snapshot[key] = props[key];
    });
    try {
      return JSON.stringify(snapshot);
    } catch (e) {
      return '';
    }
  }

  // Blank live quiz_* keys, then apply this run's values. Numbered
  // quiz_submission_N snapshots are never cleared — only appended.
  function profilePayloadForCompletion(detail, attempt) {
    var cleared = {};
    allQuizPropertyKeys().forEach(function (key) {
      cleared[key] = '';
    });

    var latest = extend({
      $first_name: detail.firstName,
      $phone_number: detail.phone,
      quiz_status: 'completed',
      quiz_result: detail.tag,
      quiz_result_title: detail.resultTitle,
      quiz_attempt_count: String(attempt)
    }, detail.properties);

    var snapshot = buildSubmissionSnapshot(detail, attempt);
    if (snapshot) latest['quiz_submission_' + attempt] = snapshot;

    return extendAllowEmpty(cleared, latest);
  }

  function identify(email, extra) {
    if (!klaviyoReady() || !email) return;
    window.klaviyo.identify(extend({ $email: email }, extra)).catch(function () {});
  }

  function identifyAllowEmpty(email, extra) {
    if (!klaviyoReady() || !email) return;
    window.klaviyo.identify(extendAllowEmpty({ $email: email }, extra)).catch(function () {});
  }

  function track(eventName, email, extra) {
    if (!klaviyoReady()) return;
    var properties = extend({}, extra);
    if (email) properties.$email = email;
    window.klaviyo.track(eventName, properties).catch(function () {});
  }

  document.addEventListener('product-quiz:email-captured', function (event) {
    var detail = event.detail || {};
    identify(detail.email, {
      $first_name: detail.firstName,
      $phone_number: detail.phone,
      quiz_sms_opt_in: detail.smsOptIn ? 'true' : 'false',
      quiz_status: 'started'
    });
  });

  document.addEventListener('product-quiz:started', function (event) {
    var detail = event.detail || {};
    track('Started Product Quiz', detail.email, { total_steps: detail.totalSteps });
  });

  document.addEventListener('product-quiz:step-answered', function (event) {
    var detail = event.detail || {};
    var answer = detail.values && detail.values.length ? detail.values.join(', ') : detail.value;
    var step = (detail.stepIndex || 0) + 1;

    identify(detail.email, {
      quiz_status: 'in_progress',
      quiz_current_step: step,
      quiz_total_steps: detail.totalSteps,
      quiz_last_question: detail.question,
      quiz_last_answer: answer
    });

    track('Answered Quiz Question', detail.email, {
      step: step,
      total_steps: detail.totalSteps,
      question: detail.question,
      answer: answer
    });
  });

  document.addEventListener('product-quiz:complete', function (event) {
    var detail = event.detail || {};
    var attempt = nextAttemptNumber(detail.email);

    identifyAllowEmpty(detail.email, profilePayloadForCompletion(detail, attempt));

    track('Completed Product Quiz', detail.email, extend({
      result_tag: detail.tag,
      result_title: detail.resultTitle,
      attempt: attempt
    }, detail.properties));
  });

  document.addEventListener('product-quiz:added-to-cart', function (event) {
    var detail = event.detail || {};
    var items = detail.items || [];

    identify(detail.email, { quiz_status: 'added_to_cart' });

    track('Added Quiz Product to Cart', detail.email, {
      items: items.map(function (item) {
        return {
          ProductName: item.title,
          ProductVariant: item.variantTitle,
          Quantity: item.quantity,
          Price: item.price
        };
      })
    });
  });

  document.addEventListener('product-quiz:bundle-link-clicked', function (event) {
    var detail = event.detail || {};
    track('Clicked Quiz Product Link', detail.email, {
      url: detail.url,
      label: detail.label,
      result_tag: detail.tag
    });
  });
})();
